import { BriefAttachment } from "./enums";

/** File extensions accepted for brief attachments. */
export const BRIEF_ATTACHMENT_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
] as const;

/** MIME content types accepted for brief attachments. */
export const BRIEF_ATTACHMENT_CONTENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
] as const;

export type BriefAttachmentContentType =
  (typeof BRIEF_ATTACHMENT_CONTENT_TYPES)[number];

/** Value for the `<input type="file" accept="..." />` attribute. */
export const BRIEF_ATTACHMENT_ACCEPT = [
  ...BRIEF_ATTACHMENT_EXTENSIONS,
  ...BRIEF_ATTACHMENT_CONTENT_TYPES,
].join(",");

const EXTENSION_TO_CONTENT_TYPE: Record<string, BriefAttachmentContentType> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
};

export const isBriefAttachmentContentType = (
  type: string,
): type is BriefAttachmentContentType =>
  BRIEF_ATTACHMENT_CONTENT_TYPES.includes(type as BriefAttachmentContentType);

export const resolveBriefAttachmentContentType = (file: {
  name?: string;
  type?: string;
}): BriefAttachmentContentType | null => {
  if (file.type && isBriefAttachmentContentType(file.type)) {
    return file.type;
  }
  const extension = file.name?.split(".").pop()?.toLowerCase();
  if (extension && Object.hasOwn(EXTENSION_TO_CONTENT_TYPE, extension)) {
    return EXTENSION_TO_CONTENT_TYPE[extension];
  }
  return null;
};

/** Shown for a brief attachment whose stored name is missing. */
export const BRIEF_ATTACHMENT_FALLBACK_NAME = "mission-brief-document.pdf";

/**
 * Folder PDFs are uploaded under. Value kept as `brief-pdfs` so existing
 * stored objects remain reachable.
 */
export const BRIEF_ATTACHMENT_PDF_FOLDER = "brief-pdfs";

/**
 * The folder a brief attachment is uploaded under, by its content type: one
 * folder per kind of file, so the bucket reads by type, with PDFs keeping
 * the folder they have always had. Clean-up only ever deletes keys under one
 * of these folders, so a key that merely appears in a brief can never point
 * the delete at some other object.
 */
export const BRIEF_ATTACHMENT_FOLDERS = {
  "application/pdf": BRIEF_ATTACHMENT_PDF_FOLDER,
  "image/jpeg": "brief-images",
  "image/png": "brief-images",
  "image/gif": "brief-images",
  "application/msword": "brief-documents",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "brief-documents",
  "application/vnd.ms-excel": "brief-spreadsheets",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "brief-spreadsheets",
  "text/csv": "brief-spreadsheets",
} as const satisfies Record<BriefAttachmentContentType, string>;

export const briefAttachmentFolder = (
  contentType: BriefAttachmentContentType,
): string => BRIEF_ATTACHMENT_FOLDERS[contentType];

const BRIEF_ATTACHMENT_FOLDER_PATTERN = [
  ...new Set(Object.values(BRIEF_ATTACHMENT_FOLDERS)),
].join("|");

/**
 * App route that opens a file kept in private storage. The key follows as
 * the path, so a brief attachment link reads `/api/files/brief-pdfs/<uuid>`.
 */
export const PRIVATE_FILES_ROUTE = "/api/files";

const BRIEF_ATTACHMENT_KEY_PATTERN = new RegExp(
  `^(?:${BRIEF_ATTACHMENT_FOLDER_PATTERN})/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$`,
  "i",
);

/** Whether an object key was issued for a brief attachment upload, in any of its folders. */
export const isBriefAttachmentKey = (key: string): boolean =>
  BRIEF_ATTACHMENT_KEY_PATTERN.test(key);

/**
 * The link that opens a brief attachment. The file is private, so the link
 * goes through the app's files route, which redirects to a short-lived signed
 * URL. The name rides along for a file that is uploaded but not saved to a
 * brief yet, since the route has nowhere else to read it from.
 */
export const briefAttachmentHref = (attachment: BriefAttachment): string =>
  `${PRIVATE_FILES_ROUTE}/${attachment.id}?name=${encodeURIComponent(attachment.name)}`;

export type BriefAttachmentColumns = {
  briefAttachments?: BriefAttachment[] | null;
};

/**
 * The files attached to a mission or deal memo brief, with a name filled in
 * for any entry stored without one, so every reader sees one shape.
 */
export const resolveBriefAttachments = ({
  briefAttachments,
}: BriefAttachmentColumns): BriefAttachment[] =>
  (briefAttachments ?? []).map((attachment) => ({
    id: attachment.id,
    name: attachment.name || BRIEF_ATTACHMENT_FALLBACK_NAME,
    ...(attachment.provider ? { provider: attachment.provider } : {}),
  }));

/**
 * Attachments that were on a brief before an edit but are not after it, so
 * the caller can tidy up their files once nothing else references them.
 */
export const removedBriefAttachments = (
  before: BriefAttachmentColumns,
  after: BriefAttachment[],
): BriefAttachment[] =>
  resolveBriefAttachments(before).filter(
    (attachment) => !after.some((kept) => kept.id === attachment.id),
  );

/**
 * Attachments on a brief after an edit that were not on it before, so the
 * caller can mark their files as saved once the edit has been committed.
 */
export const addedBriefAttachments = (
  before: BriefAttachmentColumns,
  after: BriefAttachment[],
): BriefAttachment[] => {
  const attached = new Set(
    resolveBriefAttachments(before).map((attachment) => attachment.id),
  );
  return after.filter((attachment) => !attached.has(attachment.id));
};

/**
 * Keys in an edited brief that the server has no reason to trust: neither
 * issued for a brief attachment upload nor already attached to this brief. A
 * save carrying any of these is rejected, so the stored list only ever holds
 * keys the app handed out itself.
 */
export const unknownBriefAttachmentKeys = (
  before: BriefAttachmentColumns,
  after: BriefAttachment[],
): string[] => {
  const attached = new Set(
    resolveBriefAttachments(before).map((attachment) => attachment.id),
  );
  return after
    .map((attachment) => attachment.id)
    .filter((id) => !isBriefAttachmentKey(id) && !attached.has(id));
};
